import type { KpiCardSpec } from '../kpis/card-spec';
import { ACTIVE_USERS_SCALAR_CARD } from '../kpis/active-users-scalar-card';
import { CHURN_RATE_SCALAR_CARD } from '../kpis/churn-rate-scalar-card';
import { ACCOUNT_DELETIONS_SCALAR_CARD } from '../kpis/account-deletions-scalar-card';
import { ACCOUNT_SWAPS_SCALAR_CARD } from '../kpis/account-swaps-scalar-card';
import { TOTAL_USERS_SCALAR_CARD } from '../kpis/total-users-scalar-card';
import { EMAIL_OPT_INS_SCALAR_CARD } from '../kpis/email-opt-ins-scalar-card';
import { WEEKLY_ACTIVE_USERS_SCALAR_CARD } from '../kpis/weekly-active-users-scalar-card';
import { DAILY_ACTIVE_USERS_SCALAR_CARD } from '../kpis/daily-active-users-scalar-card';
import { NEW_PAYMENT_METHODS_SCALAR_CARD } from '../kpis/new-payment-methods-scalar-card';
import { PROMOTIONS_ACTIVATED_SCALAR_CARD } from '../kpis/promotions-activated-scalar-card';
import { AUTO_REISSUED_PROMOTIONS_SCALAR_CARD } from '../kpis/auto-reissued-promotions-scalar-card';

/** "User acquisition": the period's signups, the share of them that have used
 * a feature, and the two standing counts as of the period end. Every card but
 * Feature-active users is now also carried by the page named in its own file. */
export const ACQUISITION_CARDS: KpiCardSpec[] = [
    {
        title: 'New',
        source: 'new-users',
        metric: 'newUsers',
        form: 'number',
        size: 'sm',
    },
    {
        title: 'Feature-active users',
        source: 'feature-active-users',
        metric: 'featureActiveUsers',
        form: 'rate',
        size: 'sm',
        description:
            'Of the users who signed up in the selected period, the share who have ever activated a promotion or used the carwash. The action itself is not bounded by the period.',
    },
    TOTAL_USERS_SCALAR_CARD,
    EMAIL_OPT_INS_SCALAR_CARD,
];

/** "User actions". `reissued` and `claimed` come from one source but rest on
 * different date columns: claimed counts activations created in the period,
 * reissued counts reissues that happened in it. Neither is pinned to National:
 * the backend only suppresses `available`/`redemptionRate` under a province
 * filter, never these two, so both correctly follow the page Region filter. */
export const ACTION_CARDS: KpiCardSpec[] = [
    PROMOTIONS_ACTIVATED_SCALAR_CARD,
    NEW_PAYMENT_METHODS_SCALAR_CARD,
    AUTO_REISSUED_PROMOTIONS_SCALAR_CARD,
];

/** "Retention pulse": the churn source's three metrics, none pinned, all three
 * shared with the Retention & Health page's Retention breakdown. */
export const RETENTION_CARDS: KpiCardSpec[] = [
    CHURN_RATE_SCALAR_CARD,
    ACCOUNT_DELETIONS_SCALAR_CARD,
    ACCOUNT_SWAPS_SCALAR_CARD,
];

/** The three scalars are shared card objects, not copies, so this page cannot
 * disagree with the Overview or Engagement under the same titles.
 *
 * Brand is a province grouping (QC = CT, ROC = CK), not a dimension, and this
 * KPI is province grain, so a CT/CK overlay has no metric to read. Do NOT add
 * brand-suffixed metric ids. */
export const ACTIVE_USERS_CARDS: KpiCardSpec[] = [
    ACTIVE_USERS_SCALAR_CARD,
    WEEKLY_ACTIVE_USERS_SCALAR_CARD,
    DAILY_ACTIVE_USERS_SCALAR_CARD,
    {
        title: 'Daily active users trend',
        source: 'login-activity',
        metric: 'dailyActiveUsers',
        form: 'line',
        size: 'md',
        description:
            'Users who signed in on each day of the selected period, counted once per day. Deliberate logins only: a token refresh is not a login. The points do not sum to the period total, since a user active on several days appears on each. Its last point is the Daily active users figure beside it.',
    },
];
